/**
 * Shape of the authentication slice of the store:
 *
 * - isAuthorized: boolean
 *
 * - tenant: string - the current tenant, determined from the URL
 *
 * - user:
 *     - accessToken: string
 *     - issuedAt: date/time
 *     - email: string
 *     - userID: string
 *     - uuid: string
 *
 * - error:
 *     - code: string
 *     - message: string
 *
 * - token:
 *     - email
 *     - exp - the expiration time, in Unix seconds
 *     - external_id
 *     - iat - the issued-at time, in Unix seconds
 *     - iss - the issuer of the JWT
 *     - name - the full name of the user associated with the JWT
 *     - uri
 *     - username - the login username associated with the JWT
 *     - legacy_tenant - the tenant e.g. vlv, mich, etc
 */

import { createSelector } from 'reselect';
import { Map } from 'immutable';

import {
  createFailedRequestSelectorFor,
} from 'redux-flight-control';

import { DECODE_AUTH_TOKEN } from '../constants';
import evaluateTokenExpiry from '../utils/evaluateTokenExpiry';
import getCurrentTimeInSeconds from '../utils/getCurrentTimeInSeconds';

/* --------------------------- REQUEST SELECTORS --------------------------- */

/* --------------------------- DECODE_AUTH_TOKEN --------------------------- */

export const decodeAuthTokenErrorSelector =
  createFailedRequestSelectorFor(DECODE_AUTH_TOKEN);


/* --------------------------- AUTH STORE SELECTORS --------------------------- */

const authStoreSelector = state => state.get('authentication');

export const isAuthorizedSelector = createSelector(
  authStoreSelector,
  authStore => authStore.get('isAuthorized', false),
);

export const userSelector = createSelector(
  authStoreSelector,
  authStore => authStore.get('user', Map()),
);

const tokenSelector = createSelector(
  authStoreSelector,
  authStore => authStore.get('token', Map()),
);

const tokenExpirySelector = createSelector(
  tokenSelector,
  token => token.get('exp'),
);

/**
 * Returns true if token is within 1 minute of expiry. This selector is impure.
 * `createSelector` can not be used as it will return invalid cached results.
 */
export const isTokenExpiredSelector = (state) => {
  const currentTimeInSeconds = getCurrentTimeInSeconds();
  const tokenExpiryInSeconds = tokenExpirySelector(state);
  const { expired } = evaluateTokenExpiry(currentTimeInSeconds, tokenExpiryInSeconds);
  return expired;
};

/**
 * Returns true only if token is within 15 minutes of expiry. This selector is impure.
 * `createSelector` can not be used as it will return invalid cached results.
 */
export const isTokenAlmostExpiredSelector = (state) => {
  const currentTimeInSeconds = getCurrentTimeInSeconds();
  const tokenExpiryInSeconds = tokenExpirySelector(state);
  const { almostExpired } = evaluateTokenExpiry(currentTimeInSeconds, tokenExpiryInSeconds);
  return almostExpired;
};

export const accessTokenSelector = createSelector(
  userSelector,
  authUserStore => authUserStore.get('accessToken'),
);

export const currentTenantSelector = createSelector(
  authStoreSelector,
  authStore => authStore.get('tenant', null),
);



// WEBPACK FOOTER //
// ./features/authentication/redux/selectors.js